import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronLeft, Phone, Search, LogIn } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import { useSimpleAuth } from '../../lib/authHelper';
import { NavItem, SubItem } from './types';
import SearchSuggestions from './SearchSuggestions';

const MotionNavLink = motion(NavLink);

interface DesktopNavProps {
  navItems: NavItem[];
  currentPath: string;
  activeDropdown: string | null;
  setActiveDropdown: (name: string | null) => void;
  scrollToSection: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => void;
  WHATSAPP_NUMBER: string;
  searchRef: React.RefObject<HTMLDivElement>;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;
  handleSearchSubmit: (query: string) => void;
  recentSearches: string[];
  filteredSuggestions: SubItem[];
  clearRecentSearches: () => void;
}

export default function DesktopNav({
  navItems,
  currentPath,
  activeDropdown,
  setActiveDropdown,
  scrollToSection,
  WHATSAPP_NUMBER,
  searchRef,
  searchQuery,
  setSearchQuery,
  showSuggestions,
  setShowSuggestions,
  handleSearchSubmit,
  recentSearches,
  filteredSuggestions,
  clearRecentSearches
}: DesktopNavProps) {
  const user = useSimpleAuth();

  return (
    <nav aria-label="القائمة الرئيسية" className="hidden lg:flex items-center space-x-reverse space-x-8">
      {navItems.map((item) => (
        <div 
          key={item.name} 
          className="relative group"
          onMouseEnter={() => item.hasDropdown && setActiveDropdown(item.name)}
          onMouseLeave={() => setActiveDropdown(null)}
        >
          <button 
            onClick={(e) => {
              if (item.hasDropdown) {
                e.preventDefault();
                setActiveDropdown(activeDropdown === item.name ? null : item.name);
              } else {
                scrollToSection(e, item.href);
              }
            }}
            aria-label={item.hasDropdown ? `فتح قائمة ${item.name}` : item.name}
            aria-expanded={item.hasDropdown ? activeDropdown === item.name : undefined}
            aria-haspopup={item.hasDropdown ? "menu" : undefined}
            className={`font-bold flex items-center gap-1.5 py-4 transition-all duration-300 relative group/link ${
              currentPath === item.href || (item.subItems?.some(s => s.href === currentPath))
                ? 'text-brand-accent' 
                : 'text-brand-dark hover:text-brand-accent'
            }`}
          >
            {item.name}
            {item.hasDropdown && (
              <motion.div
                animate={{ rotate: activeDropdown === item.name ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ChevronDown size={16} className={activeDropdown === item.name || item.subItems?.some(s => s.href === currentPath) ? 'text-brand-accent' : 'text-slate-400'} />
              </motion.div>
            )}
            <span className={`absolute bottom-3 right-0 h-0.5 bg-brand-accent transition-all duration-300 ${
              activeDropdown === item.name || currentPath === item.href || item.subItems?.some(s => s.href === currentPath) ? 'w-full' : 'w-0 group-hover/link:w-full'
            }`} />
          </button>

          <AnimatePresence>
            {item.hasDropdown && activeDropdown === item.name && (
              <motion.div
                layout
                initial={{ opacity: 0, y: -10, filter: 'blur(5px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -10, filter: 'blur(5px)' }}
                transition={{ 
                  duration: 0.3, 
                  ease: [0.22, 1, 0.36, 1] 
                }}
                className="absolute top-full right-0 w-[24rem] mt-2 bg-white border border-slate-100 shadow-[0_20px_50px_rgba(0,0,0,0.12)] rounded-2xl overflow-hidden py-3.5 z-[60] backdrop-blur-xl"
                role="menu"
              >
                <div className="flex flex-col gap-1.5 px-2.5">
                  {item.subItems?.map((subItem, idx) => (
                    <MotionNavLink
                      layout
                      key={subItem.name}
                      to={subItem.href}
                      onClick={(e) => scrollToSection(e, subItem.href)}
                      target={subItem.href.startsWith('http') ? '_blank' : '_self'}
                      rel={subItem.href.startsWith('http') ? 'noopener noreferrer' : ''}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.04, ease: 'easeOut' }}
                      role="menuitem"
                      className={({ isActive }) => `px-3.5 py-3 transition-all text-right rounded-xl flex items-center justify-between group/item border border-transparent ${
                        isActive 
                          ? 'text-brand-accent bg-brand-accent/5 border-brand-accent/10' 
                          : 'text-slate-700 hover:text-brand-dark hover:bg-slate-50/80 hover:border-slate-100'
                      }`}
                    >
                      <span className="flex items-center gap-3.5 transition-transform duration-300 group-hover/item:-translate-x-1">
                        <span className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 group-hover/item:bg-brand-accent/15 group-hover/item:text-brand-dark group-hover/item:scale-105 group-hover/item:rotate-6 ${
                          currentPath === subItem.href 
                            ? 'bg-brand-accent/20 text-brand-dark border border-brand-accent/20 shadow-sm' 
                            : 'bg-slate-50 text-brand-accent border border-slate-100/60'
                        }`}>
                          {subItem.icon && <subItem.icon size={20} />}
                        </span>
                        <span className="flex flex-col text-right">
                          <span className={`text-sm font-bold transition-colors ${
                            currentPath === subItem.href ? 'text-brand-dark font-black' : 'text-slate-800 group-hover/item:text-brand-dark'
                          }`}>{subItem.name}</span>
                          {subItem.description && (
                            <span className="text-xs text-slate-400 font-medium mt-0.5 line-clamp-1 group-hover/item:text-slate-500 transition-colors">
                              {subItem.description}
                            </span>
                          )}
                        </span>
                      </span>
                      <span className={`transition-all duration-300 translate-x-2 group-hover/item:translate-x-0 ${currentPath === subItem.href ? 'opacity-100' : 'opacity-0 group-hover/item:opacity-100'}`}>
                        <ChevronLeft size={16} className="text-brand-accent" />
                      </span>
                    </MotionNavLink>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
      
      <div className="flex items-center gap-4 mr-4">
        <a 
          href={`tel:${WHATSAPP_NUMBER}`}
          aria-label={`الاتصال الهاتفي بنا على الرقم ${WHATSAPP_NUMBER}`}
          className="flex items-center gap-2 text-brand-dark hover:text-brand-accent transition-colors"
        >
          <Phone size={18} />
          <span className="text-sm font-bold" dir="ltr">+{WHATSAPP_NUMBER}</span>
        </a>
        <Link 
          to={user ? (user.email === 'm.0543125382@gmail.com' ? '/admin/dashboard' : '/dashboard') : '/login'}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1B4D3E]/10 hover:bg-brand-accent hover:text-brand-dark text-brand-dark text-xs font-bold rounded-lg border border-brand-accent/30 transition-all duration-300 transform active:scale-95 whitespace-nowrap"
        >
          <LogIn size={13} />
          <span>{user ? 'لوحة التحكم' : 'تسجيل الدخول'}</span>
        </Link>
        <div className="relative" ref={searchRef} role="search">
          <input 
            type="text" 
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder="بحث عن خدمة ..." 
            aria-label="ابحث عن الخدمات"
            className="bg-slate-100 border border-slate-200 text-brand-dark text-sm rounded-full py-2 px-4 pr-10 focus:outline-none focus:border-brand-accent focus:bg-white/20 transition-all w-48 focus:w-64"
            onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(searchQuery)}
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />

          <SearchSuggestions 
            showSuggestions={showSuggestions}
            searchQuery={searchQuery}
            recentSearches={recentSearches}
            filteredSuggestions={filteredSuggestions}
            handleSearchSubmit={handleSearchSubmit}
            clearRecentSearches={clearRecentSearches}
            scrollToSection={scrollToSection}
            setShowSuggestions={setShowSuggestions}
            setSearchQuery={setSearchQuery}
          />
        </div>
      </div>
    </nav>
  );
}
